import React, { type ReactNode } from 'react';
import { Button } from 'components/atoms/Button';
import ExclaimationTriangleIcon from 'components/atoms/SVG/ExclaimationTrinagleIcon';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: React.ErrorInfo;
}

/**
 * Error boundary for Activation Flow
 * Catches errors in step rendering and prevents full app crash
 *
 * If a step fails to render:
 * 1. Shows user-friendly error message
 * 2. Logs error details for debugging
 * 3. Provides recovery options (refresh or restart)
 *
 * TODO: Send errors to logging service (Sentry/etc) in production
 */
class ActivationFlowErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Log to console in development
    if (process.env.NODE_ENV === 'development') {
      console.error('❌ [Activation Flow Error]', error);
      console.error('Component stack:', errorInfo.componentStack);
    }

    // TODO: Send to error tracking service in production
    // Example: Sentry.captureException(error, { extra: errorInfo });

    this.setState({ errorInfo });
  }

  handleRefresh = () => {
    window.location.reload();
  };

  handleRestart = () => {
    // Reset error state to try rendering again
    this.setState({ hasError: false, error: undefined, errorInfo: undefined });
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex flex-col items-center justify-center p-8 min-h-[400px]">
          <ExclaimationTriangleIcon className="w-16 h-16 text-red-500 mb-4" />

          <h2 className="text-2xl font-bold text-primary-900 mb-2">
            Something went wrong
          </h2>

          <p className="text-primary-600 text-center mb-6 max-w-md">
            We encountered an error while loading this step. Please try
            refreshing the page or contact support if the issue persists.
          </p>

          {process.env.NODE_ENV === 'development' && this.state.error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg max-w-2xl overflow-auto">
              <p className="font-mono text-sm text-red-800">
                {this.state.error.toString()}
              </p>
            </div>
          )}

          <div className="flex gap-3">
            <Button onClick={this.handleRefresh} structure="primary">
              Refresh Page
            </Button>

            <Button onClick={this.handleRestart} structure="secondary">
              Try Again
            </Button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
export default ActivationFlowErrorBoundary;
